import React, { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from 'react-helmet-async';
import GlobalStyles from 'styles/GlobalStyles';

/*
 * This is the entry point component of this project. You can change the below exported default App component to any of
 * the prebuilt landing page components by uncommenting their import and export lines respectively.
 * See one of the landing page components to better understand how to import and render different components (Always
 * make sure if you are building your own page, the root component should be the AnimationRevealPage component. You can
 * disable the animation by using the disabled prop.
 *
 * The App component below is using React router to render the landing page that you see on the live demo website
 * and the component previews.
 *
 */
/* Use AnimationRevealPage as a wrapper component for your pages if you are building a custom one yourself */
// import AnimationRevealPage from "helpers/AnimationRevealPage.js";
/*
 * Hero section is the top most section on the page. It contains the header as well.
 * So you dont need to import headers
 * separately
 */
// import Hero from "components/hero/TwoColumnWithVideo.js";
// import Hero from "components/hero/ToolboneHome.js";
// import Hero from "components/hero/TwoColumnWithFeaturesAndTestimonial.js";
// import Hero from "components/hero/TwoColumnWithPrimaryBackground.js";
// import Hero from "components/hero/FullWidthWithImage.js";
// import Hero from "components/hero/BackgroundAsImage.js";
// import Hero from "components/hero/BackgroundAsImageWithCenteredContent.js";
// import Features from "components/features/ThreeColSimple.js";
// import Features from "components/features/ThreeColWithSideImage.js";
// import Features from "components/features/ThreeColWithSideImageWithPrimaryBackground.js";
// import Features from "components/features/VerticalWithAlternateImageAndText.js";
// import Features from "components/features/DashedBorderSixFeatures";
// import MainFeature from "components/features/TwoColWithButton.js";
// import MainFeature from "components/features/TwoColSingleFeatureWithStats.js";
// import MainFeature2 from "components/features/TwoColSingleFeatureWithStats2.js";
// import MainFeature from "components/features/ToolboneAbout.js";
// import FeatureWithSteps from "components/features/TwoColWithSteps.js";
// import FeatureStats from "components/features/ThreeColCenteredStatsPrimaryBackground.js";
// import Pricing from "components/pricing/ThreePlans.js";
// import Pricing from "components/pricing/ThreePlansWithHalfPrimaryBackground.js";
// import Pricing from "components/pricing/TwoPlansWithDurationSwitcher.js";
// import SliderCard from "components/cards/ThreeColSlider.js";
// import TrendingCard from "components/cards/TwoTrendingPreviewCardsWithImage.js";
// import Portfolio from "components/cards/PortfolioTwoCardsWithImage.js";
// import TabGrid from "components/cards/TabCardGrid.js";
// import Blog from "components/blogs/ThreeColSimpleWithImage.js";
// import Blog from "components/blogs/ThreeColSimpleWithImageAndDashedBorder.js";
// import Blog from "components/blogs/PopularAndRecentBlogPosts.js";
// import Blog from "components/blogs/GridWithFeaturedPost.js";
// import Testimonial from "components/testimonials/TwoColumnWithImage.js";
// import Testimonial from "components/testimonials/TwoColumnWithImageAndProfilePictureReview.js";
// import Testimonial from "components/testimonials/TwoColumnWithImageAndRating.js";
// import Testimonial from "components/testimonials/ThreeColumnWithProfileImage.js";
// import Testimonial from "components/testimonials/SimplePrimaryBackground.js";
// import FAQ from "components/faqs/SimpleWithSideImage.js";
// import FAQ from "components/faqs/SingleCol.js";
// import FAQ from "components/faqs/TwoColumnPrimaryBackground.js";
// import ContactUsForm from "components/forms/SimpleContactUs.js";
// import ContactUsForm from "components/forms/TwoColContactUsWithIllustration.js";
// import SubscribeNewsLetterForm from "components/forms/SimpleSubscribeNewsletter.js";
//
// import GetStarted from "components/cta/GetStarted.js";
// import GetStarted from "components/cta/GetStartedLight.js";
// import DownloadApp from "components/cta/DownloadApp.js";
// import Footer from "components/footers/SimpleFiveColumn.js";
// import Footer from "components/footers/FiveColumnWithInputForm.js";
// import Footer from "components/footers/FiveColumnWithBackground.js";
// import Footer from "components/footers/FiveColumnDark.js";
// import Footer from "components/footers/MiniCenteredFooter.js";

import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import TagManager from "react-gtm-module"; // ✅ Import GTM
import ErrorBoundary from "components/misc/ErrorBoundary.js";
import { ThemeProvider } from "contexts/ThemeContext";
import FloatingThemeSwitcher from "components/misc/FloatingThemeSwitcher";
import ParticleBackground from "components/misc/ParticleBackground";

/* Ready Made Pages (from demos folder) */
// ⚡ Lazy load pages for better performance and code splitting
const ToolboneLandingPage = lazy(() => import("demos/ToolboneLandingPage.js"));

/* Inner Pages - Lazy loaded */
const ToolbonePrivacyPolicy = lazy(() => import("pages/legal/ToolbonePrivacyPolicy"));
const ToolboneContactUs = lazy(() => import("pages/ToolboneContactUs"));
const ToolboneTerms = lazy(() => import("pages/legal/ToolboneTerms"));

/* Product Pages - Lazy loaded */
const ProductsIndex = lazy(() => import("pages/products/index"));
const Workentro = lazy(() => import("pages/products/Workentro/index"));
const ArchiCore = lazy(() => import("pages/products/ArchiCore/index"));
const MuseSync = lazy(() => import("pages/products/MuseSync/index"));
const MuseNote = lazy(() => import("pages/products/MuseNote/index"));
const MuseTuner = lazy(() => import("pages/products/MuseTuner/index"));
const Splodge = lazy(() => import("pages/products/Splodge/index"));
const TrailerPro = lazy(() => import("pages/products/TrailerPro/index"));
const Website = lazy(() => import("pages/products/Website/index"));

/* App Account Pages */
const AccountDeletion = lazy(() => import("pages/app/AccountDeletion"));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid rgba(255, 255, 255, 0.3)',
      borderTop: '4px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}/>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export default function App() {
    // If you want to disable the animation just use the disabled `prop` like below on your page's component
    // return <AnimationRevealPage disabled>xxxxxxxxxx</AnimationRevealPage>;
    // ✅ Initialize GTM when app loads
    useEffect(() => {
        TagManager.initialize({ gtmId: "G-9G9PJ8J77C" }); // Replace with your GTM ID
    }, []);


    return (
        <ErrorBoundary>
            <ThemeProvider>
                <ParticleBackground enabled={true} />
                <FloatingThemeSwitcher />
                <HelmetProvider>
                    <GlobalStyles/>
                    <Router>
                        <Suspense fallback={<PageLoader/>}>
                            <Routes>
                            <Route path="/" element={<ToolboneLandingPage/>}/>
                            <Route path="/home" element={<ToolboneLandingPage/>}/>
                            <Route path="/about" element={<ToolboneLandingPage/>}/>
                            <Route path="/features" element={<ToolboneLandingPage/>}/>
                            <Route path="/pricing" element={<ToolboneLandingPage/>}/>
                            <Route path="/workflow" element={<ToolboneLandingPage/>}/>
                            <Route path="/stories" element={<ToolboneLandingPage/>}/>
                            <Route path="/faqs" element={<ToolboneLandingPage/>}/>

                            <Route path="/#home" element={<ToolboneLandingPage/>}/>
                            <Route path="/#about" element={<ToolboneLandingPage/>}/>
                            <Route path="/#features" element={<ToolboneLandingPage/>}/>
                            <Route path="/#pricing" element={<ToolboneLandingPage/>}/>
                            <Route path="/#workflow" element={<ToolboneLandingPage/>}/>
                            <Route path="/#stories" element={<ToolboneLandingPage/>}/>
                            <Route path="/#faqs" element={<ToolboneLandingPage/>}/>

                            <Route path="/contact-us" element={<ToolboneContactUs/>}/>
                            <Route path="/terms-of-service" element={<ToolboneTerms/>}/>
                            <Route path="/privacy-policy" element={<ToolbonePrivacyPolicy/>}/>

                            {/* Product Pages */}
                            <Route path="/products" element={<ProductsIndex/>}/>
                            <Route path="/products/workentro" element={<Workentro/>}/>
                            <Route path="/products/archicore" element={<ArchiCore/>}/>
                            <Route path="/products/musesync" element={<MuseSync/>}/>
                            <Route path="/products/musetuner" element={<MuseTuner/>}/>
                            <Route path="/products/musenote" element={<MuseNote/>}/>
                            <Route path="/products/splodge" element={<Splodge/>}/>
                            <Route path="/products/trailerpro" element={<TrailerPro/>}/>
                            <Route path="/products/website" element={<Website/>}/>

                            {/* App Account Pages (Google Play account deletion requirement) */}
                            <Route path="/app/:appName/account" element={<AccountDeletion/>}/>

                            {/* Legacy Ad Pages */}
                            <Route path="/ads/musesync" element={<MuseSync/>}/>
                            <Route path="/ads/musenote" element={<MuseNote/>}/>
                            <Route path="/ads/musetuner" element={<MuseTuner/>}/>
                            <Route path="/ads/splodge" element={<Splodge/>}/>
                            <Route path="/ads/trailerpro" element={<TrailerPro/>}/>
                            </Routes>
                        </Suspense>
                    </Router>
                </HelmetProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

// export default EventLandingPage;
// export default HotelTravelLandingPage;
// export default AgencyLandingPage;
// export default SaaSProductLandingPage;
// export default RestaurantLandingPage;
// export default ServiceLandingPage;
// export default HostingCloudLandingPage;

// export default LoginPage;
// export default SignupPage;
// export default PricingPage;
// export default AboutUsPage;
// export default ContactUsPage;
// export default BlogIndexPage;
// export default TermsOfServicePage;
// export default PrivacyPolicyPage;

// export default MainLandingPage;
